// Format time display
export const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// Send proctoring data to the backend
export const sendProctoringData = async (
  sessionId,
  examCandidateAPI,
  eventData
) => {
  if (!sessionId) return;

  try {
    return await examCandidateAPI.sendProctoringData(sessionId, eventData);
  } catch (error) {
    console.error("Failed to send proctoring data:", error);
    return null;
  }
};

// Handle visibility changes (tab switching)
export const handleVisibilityChange = async (
  document,
  lastTabActiveTimeRef,
  tabSwitchCountRef,
  sessionId,
  exam,
  examCandidateAPI,
  showCheatingWarning
) => {
  if (!exam?.proctoring?.enabled) return;

  // Make sure we have a valid exam with proctoring settings
  if (!exam?.proctoring?.trackTabSwitching) return;

  if (document.visibilityState === "hidden") {
    const now = Date.now();
    lastTabActiveTimeRef.current = now;

    // Log tab switch
    tabSwitchCountRef.current += 1;

    // Send tab switch event to server
    const response = await sendProctoringData(sessionId, examCandidateAPI, {
      type: "tabSwitch",
      timestamp: new Date().toISOString(),
      details: "Tab switched/minimized",
    });

    // Issue warning if allowed
    const warningMsg =
      "Switching tabs or minimizing the browser is not allowed during the exam.";
    showCheatingWarning(warningMsg, response?.data?.disqualified);
  } else if (lastTabActiveTimeRef.current > 0) {
    const timeAway = Date.now() - lastTabActiveTimeRef.current;

    // Send tab return event
    await sendProctoringData(sessionId, examCandidateAPI, {
      type: "focusReturn",
      timestamp: new Date().toISOString(),
      details: `Returned after ${Math.round(timeAway / 1000)}s`,
    });
  }
};

// Capture screenshot and send to server
export const captureAndSendScreenshot = async (
  sessionId,
  examCandidateAPI,
  videoRef,
  screenshotCanvasRef,
  lastScreenshotTimeRef,
  cameraActive,
  exam,
  reason = "periodic"
) => {
  if (
    !cameraActive ||
    !exam?.proctoring?.enabled ||
    !sessionId ||
    !exam?.proctoring?.captureImages
  )
    return;

  try {
    const now = Date.now();

    // Limit screenshot frequency to avoid overloading
    if (
      now - lastScreenshotTimeRef.current <
        (exam.proctoring.captureFrequency || 10000) &&
      reason === "periodic"
    ) {
      return;
    }

    lastScreenshotTimeRef.current = now;

    // Capture video frame
    let faceImage = null;

    if (videoRef.current) {
      const canvas = screenshotCanvasRef.current;
      const video = videoRef.current;

      // Set canvas to video dimensions
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // Draw the video frame on canvas
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the image as base64 data URL (reducing quality to save bandwidth)
      faceImage = canvas.toDataURL("image/jpeg", 0.7);
    }

    // Send to server
    await examCandidateAPI.sendProctoringData(sessionId, {
      type: "screenshot",
      reason: reason,
      timestamp: new Date().toISOString(),
      details: `Screenshot capture: ${reason}`,
      imageData: faceImage,
    });
  } catch (error) {
    console.error("Failed to capture or send screenshot:", error);
  }
};

// Handle keyboard shortcuts that might indicate cheating
export const handleKeyDown = async (
  event,
  exam,
  keySequenceRef,
  sessionId,
  examCandidateAPI,
  videoRef,
  screenshotCanvasRef,
  lastScreenshotTimeRef,
  showCheatingWarning
) => {
  if (!exam?.proctoring?.enabled) return;

  // Track key sequence for detecting patterns like Ctrl+C, Ctrl+V, etc.
  keySequenceRef.current.push(event.key);
  if (keySequenceRef.current.length > 5) keySequenceRef.current.shift();

  // Detect potential cheating key combinations
  if (
    (event.ctrlKey || event.metaKey) &&
    ["c", "v", "a", "p", "s", "PrintScreen"].includes(event.key)
  ) {
    const action = {
      c: "Copy",
      v: "Paste",
      a: "Select All",
      p: "Print",
      s: "Save",
      PrintScreen: "Screenshot",
    }[event.key];

    const response = await sendProctoringData(sessionId, examCandidateAPI, {
      type: "warning",
      timestamp: new Date().toISOString(),
      details: `Potential cheating detected: ${action} shortcut used (${
        event.ctrlKey ? "Ctrl" : "Meta"
      }+${event.key})`,
    });

    captureAndSendScreenshot(
      sessionId,
      examCandidateAPI,
      videoRef,
      screenshotCanvasRef,
      lastScreenshotTimeRef,
      true,
      exam,
      "keyboard_shortcut"
    );

    // Add warning for certain actions
    if (["c", "p", "PrintScreen"].includes(event.key)) {
      const warningMsg = `Using ${action} shortcuts is not allowed during the exam.`;
      showCheatingWarning(warningMsg, response?.data?.disqualified);

      // Prevent default for these actions
      event.preventDefault();
      return false;
    }
  }
};

// Show warning to user and handle disqualification if necessary
export const showCheatingWarning = (
  toast,
  navigate,
  warningMessage,
  isDisqualified
) => {
  if (isDisqualified) {
    toast({
      title: "You have been disqualified",
      description:
        "You have been disqualified from the exam due to multiple violations.",
      variant: "destructive",
      duration: 5000,
    });

    // Redirect to completed page
    setTimeout(() => {
      navigate("/candidate");
    }, 3000);

    return;
  }

  toast({
    title: "Warning",
    description: warningMessage,
    variant: "destructive",
    duration: 5000,
  });
};
